import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

const services = [
  {
    icon: 'fa-solid fa-boxes-packing',
    title: '1. Demand Forecasting & Inventory Optimization',
    desc: 'Deploy AI-driven predictive algorithms to forecast demand accurately, optimize safety stock levels, and eliminate overstock and stockout scenarios.',
  },
  {
    icon: 'fa-solid fa-truck-fast',
    title: '2. Logistics & Fleet Route Optimization',
    desc: 'Utilize real-time spatial analytics and dynamic routing to reduce transit times, trim fuel costs, and ensure on-time last-mile deliveries.',
  },
  {
    icon: 'fa-solid fa-warehouse',
    title: '3. Warehouse Management System (WMS) Integration',
    desc: 'Automate picking, packing, putaway, and inventory tracking by integrating modern WMS and RFID/IoT technologies into core operations.',
  },
  {
    icon: 'fa-solid fa-network-wired',
    title: '4. End-to-End Control Tower Visibility',
    desc: 'Build centralized control tower dashboards for real-time tracking of raw materials, work-in-progress, and finished goods across multi-tier networks.',
  },
  {
    icon: 'fa-solid fa-handshake-angle',
    title: '5. Supplier Relationship & Procurement Portals',
    desc: 'Streamline vendor onboarding, automated purchase order generation, dynamic supplier scoring, and procurement workflows.',
  },
  {
    icon: 'fa-solid fa-chart-line-up',
    title: '6. Supply Chain Analytics & Risk Mitigation',
    desc: 'Identify geopolitical, weather, and market supply bottlenecks using predictive risk models to build resilient fallback sourcing options.',
  },
  {
    icon: 'fa-solid fa-clipboard-check',
    title: '7. Order Management Systems (OMS)',
    desc: 'Unify multi-channel order capture, automated fulfillment routing, split-shipment rules, and customer tracking notifications.',
  },
  {
    icon: 'fa-solid fa-leaf',
    title: '8. Sustainable & Green Logistics',
    desc: 'Track and lower carbon footprints, optimize package volume-to-weight ratios, and support eco-friendly reverse logistics networks.',
  },
  {
    icon: 'fa-solid fa-rotate-left',
    title: '9. Reverse Logistics & Returns Processing',
    desc: 'Streamline return authorizations, refurbishment triage, warranty tracking, and customer refund processing to reclaim lost margins.',
  },
  {
    icon: 'fa-solid fa-barcode',
    title: '10. IoT Cold Chain & Asset Tracking',
    desc: 'Monitor temperature-sensitive shipments and high-value cargo in transit using IoT sensors and automated alert rules.',
  },
];

const whyChooseUs = [
  'Significant reduction in carrying costs and excess inventory',
  'Real-time, multi-tier supply chain visibility and control',
  'Dynamic routing algorithms that lower fuel and transport costs',
  'Proactive risk detection to prevent supply chain disruptions',
  'Seamless integration with ERP (SAP, Oracle, NetSuite) systems',
  'Higher OTIF (On-Time, In-Full) delivery performance metrics',
  'Automated multi-channel order fulfillment workflows',
  'End-to-end IoT sensor tracking for sensitive assets',
  'Scalable cloud infrastructure built for global trade networks',
  'Data-backed vendor management and procurement automation',
];

const processSteps = [
  { step: '01', title: 'Supply Chain Assessment', desc: 'Map current logistics workflows, supplier nodes, inventory velocity, and operational bottlenecks.' },
  { step: '02', title: 'Data Architecture & Strategy', desc: 'Design centralized telemetry pipelines, ERP/WMS integrations, and key KPI benchmarks.' },
  { step: '03', title: 'Optimization Engine Setup', desc: 'Deploy AI demand models, dynamic routing logic, and inventory buffer rules.' },
  { step: '04', title: 'Control Tower Integration', desc: 'Unify real-time carrier data, warehouse statuses, and supplier feeds into central dashboards.' },
  { step: '05', title: 'Pilot Testing & Validation', desc: 'Conduct simulation runs across select shipping lanes and warehouses to measure cost savings.' },
  { step: '06', title: 'Full Rollout & Monitoring', desc: 'Scale across global networks with continuous algorithm tuning and 24/7 system support.' },
];

const techStack = [
  { category: 'Enterprise ERP & Supply Chain Platforms', items: ['SAP S/4HANA', 'Oracle SCM', 'Blue Yonder', 'Manhattan Associates', 'Infor Nexus'] },
  { category: 'Route Optimization & Tracking', items: ['HERE Technologies', 'Project44', 'FourKites', 'Samsara', 'Google Maps Platform'] },
  { category: 'IoT & Telemetry', items: ['AWS IoT Core', 'Azure IoT Hub', 'RFID Systems', 'Bluetooth LE Gateways'] },
  { category: 'Analytics & Optimization', items: ['Python (Pandas / SciPy)', 'Databricks', 'Power BI', 'Snowflake', 'Tableau'] },
];

// Animation Variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.12 },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } },
};

const SupplyChainOptimization = () => {
  return (
    <div className="service-page text-white overflow-hidden">
      {/* 1. HERO SECTION */}
      <section className="hero-sub-section text-center d-flex align-items-center justify-content-center">
        <div className="container py-5">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="badge rounded-pill bg-outline-cyan px-3 py-2 mb-3 glow-badge">
              Lidlbritain Digital Transformation
            </span>
            <h1 className="display-4 fw-bold mb-3">
              Supply Chain <span className="gradient-text-cyan">Optimization</span>
            </h1>
            <p className="lead mx-auto opacity-75 max-width-800">
              At <strong>Lidlbritain</strong>, we transform fragile logistics into resilient, data-driven value chains. From AI-powered demand forecasting to real-time control towers, we optimize operations to reduce costs and ensure OTIF delivery.
            </p>
            <div className="mt-4">
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} className="d-inline-block">
                <Link to="/contact" className="btn gradient-btn px-4 py-2 rounded-pill">
                  Optimize Your Supply Chain &rarr;
                </Link>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* 2. SERVICES GRID SECTION */}
      <section className="py-5">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="fw-bold">
              Supply Chain Solutions by <span className="gradient-text-orange">Lidlbritain</span>
            </h2>
            <div className="title-line mx-auto mt-2"></div>
          </div>

          <motion.div
            className="row g-4"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-50px' }}
          >
            {services.map((item, index) => (
              <div key={index} className="col-md-6 col-lg-4">
                <motion.div
                  variants={cardVariants}
                  whileHover={{ y: -8, transition: { duration: 0.2 } }}
                  className="service-card p-4 rounded-4 h-100 border border-secondary border-opacity-25 bg-glass"
                >
                  <div className="icon-box mb-3 d-flex align-items-center justify-content-center floating-icon">
                    <i className={`${item.icon} fs-3 text-cyan`}></i>
                  </div>
                  <h5 className="fw-bold mb-3">{item.title}</h5>
                  <p className="text-light opacity-75 small leading-relaxed">{item.desc}</p>
                </motion.div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* 3. WHY CHOOSE US & TECH STACK SECTION */}
      <section className="py-5 bg-dark-overlay">
        <div className="container">
          <div className="row g-5 align-items-center">
            {/* Why Choose Lidlbritain */}
            <motion.div
              className="col-lg-6"
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h3 className="fw-bold mb-4">
                Why Partner With <span className="gradient-text-cyan">Lidlbritain?</span>
              </h3>
              <div className="row g-3">
                {whyChooseUs.map((reason, idx) => (
                  <div key={idx} className="col-sm-6">
                    <div className="d-flex align-items-center gap-2">
                      <i className="fa-solid fa-circle-check text-orange pulse-icon"></i>
                      <span className="small opacity-90">{reason}</span>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Tools & Tech Stack */}
            <motion.div
              className="col-lg-6"
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="p-4 rounded-4 border border-secondary border-opacity-25 bg-glass glow-box">
                <h4 className="fw-bold mb-4">Supply Chain Tech Ecosystem</h4>
                {techStack.map((tech, idx) => (
                  <div key={idx} className="mb-3">
                    <h6 className="text-cyan mb-2 fw-semibold">{tech.category}</h6>
                    <div className="d-flex flex-wrap gap-2">
                      {tech.items.map((item, i) => (
                        <motion.span
                          whileHover={{ scale: 1.1 }}
                          key={i}
                          className="badge bg-secondary bg-opacity-22 border border-secondary border-opacity-50 text-light fw-normal py-2 px-3"
                        >
                          {item}
                        </motion.span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 4. PROCESS SECTION */}
      <section className="py-5">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="fw-bold">
              The Lidlbritain <span className="gradient-text-orange">Optimization Framework</span>
            </h2>
            <div className="title-line mx-auto mt-2"></div>
          </div>

          <motion.div
            className="row g-4"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {processSteps.map((stepItem, index) => (
              <div key={index} className="col-md-6 col-lg-4">
                <motion.div
                  variants={cardVariants}
                  whileHover={{ scale: 1.03 }}
                  className="process-card p-4 rounded-4 h-100 bg-glass border border-secondary border-opacity-25 position-relative"
                >
                  <span className="step-number fw-bold text-cyan mb-2 d-block">{stepItem.step}</span>
                  <h5 className="fw-bold mb-2">{stepItem.title}</h5>
                  <p className="text-light opacity-75 small mb-0">{stepItem.desc}</p>
                </motion.div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* 5. CALL TO ACTION SECTION */}
      <section className="py-5 my-4">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="p-5 rounded-5 cta-banner text-center position-relative overflow-hidden"
          >
            <h2 className="fw-bold mb-3">Build an Agile & Resilient Supply Chain with Lidlbritain</h2>
            <p className="lead opacity-90 mx-auto mb-4 max-width-800">
              Eliminate supply bottlenecks, lower operational transport costs, and gain full multi-tier visibility across your network.
            </p>
            <motion.div whileHover={{ scale: 1.08 }} whileTap={{ scale: 0.95 }} className="d-inline-block">
              <Link to="/contact" className="btn gradient-btn btn-lg px-5 py-3 rounded-pill fw-semibold">
                Consult with a Supply Chain Expert &rarr;
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default SupplyChainOptimization;